/**
 * Convert with AI: the step-by-step collector for the Figma -> real-code path.
 *
 * Airbnb-style, one thing per screen:
 *   1. Figma link (required)
 *   2. AI tool (Claude or ChatGPT)
 *   (No name step: the site keeps its generated address and can be renamed later.)
 *
 * It owns no deploy mechanics: the captured link / tool / name live in
 * CreateFlow state (lifted via props), and on the final step it converges into
 * CreateFlow's existing agent screen (onConvergeAgent), which mints the token,
 * shows the connector-first prompt and goes live the moment the deploy lands.
 */
import React, { useState } from 'react';

const AI_TOOLS = [
  { id: 'claude', label: 'Claude', desc: 'claude.ai, Claude Code or Cowork. They all use the same prompt' },
  { id: 'chatgpt', label: 'ChatGPT', desc: 'chatgpt.com or Codex desktop' },
];

function BuildWithAI({ figmaLink, setFigmaLink, aiTool, setAiTool, onConvergeAgent }) {
  const [stepIdx, setStepIdx] = useState(1); // 1 link, 2 tool

  const canContinue =
    stepIdx === 1 ? !!figmaLink.trim() : !!aiTool;

  const next = () => {
    if (!canContinue) return;
    if (stepIdx < 2) setStepIdx(stepIdx + 1);
    else onConvergeAgent();
  };
  const back = () => setStepIdx((i) => Math.max(1, i - 1));

  return (
    <div className="ut-create-flow">
      <div className="createflow-pips" aria-hidden="true">
        {[1, 2].map((n) => (
          <span key={n} className={`createflow-pip${n === stepIdx ? ' active' : ''}${n < stepIdx ? ' done' : ''}`} />
        ))}
      </div>

      {stepIdx === 1 && (
        <div>
          <div className="ut-create-section-label">Paste your Figma link</div>
          <input
            className="aero-input createflow-input-lg"
            type="url"
            placeholder="https://www.figma.com/design/…"
            value={figmaLink}
            onChange={(e) => setFigmaLink(e.target.value)}
            autoFocus
          />
          <div className="ut-create-hint">
            Right-click a frame in Figma → Copy link to selection. One frame at a
            time gives the best result.
          </div>
        </div>
      )}

      {stepIdx === 2 && (
        <div>
          <div className="ut-create-section-label">Which AI tool will you use?</div>
          <div className="ut-create-source-grid" role="radiogroup" aria-label="AI tool">
            {AI_TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`ut-create-source-card${aiTool === t.id ? ' selected' : ''}`}
                onClick={() => setAiTool(t.id)}
                aria-pressed={aiTool === t.id}
              >
                <span className="ut-create-source-title">{t.label}</span>
                <span className="ut-create-source-desc">{t.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="createflow-step-actions">
        {stepIdx > 1 && (
          <button type="button" className="aero-btn aero-btn-secondary" onClick={back}>
            Back
          </button>
        )}
        <button type="button" className="aero-btn aero-btn-primary" onClick={next} disabled={!canContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

export default BuildWithAI;
